import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { showToast } from '../lib/utils';

export interface Player {
  id: string;
  name: string;
  number: string;
  positions: string[];
  image: string;
  bio: string;
  hometown: string;
  created_at: string;
  role: 'player' | 'gameday';
  title?: string;
}

interface Game {
  id: string;
  date: string;
  time: string;
  time2?: string | null;
  opponent: string;
  location: string;
  venue: string;
  address?: string;
  score?: {
    game1?: {
      cardinals: number;
      opponent: number;
    };
    game2?: {
      cardinals: number;
      opponent: number;
    };
    cardinals?: number;
    opponent?: number;
  };
  created_at: string;
  isDoubleHeader: boolean;
}

interface Sponsor {
  id: string;
  name: string;
  logo: string;
  website: string;
  description: string;
  active: boolean;
  tier: 'gold' | 'silver';
  display_order?: number;
  created_at?: string;
}

interface StoreState {
  players: Player[];
  games: Game[];
  sponsors: Sponsor[];
  isAuthenticated: boolean;
  authError: string | null;
  isLoading: boolean;
  fetchData: () => Promise<void>;
  setIsAuthenticated: (value: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  deletePlayer: (id: string) => Promise<void>;
  deleteGame: (id: string) => Promise<void>;
  deleteSponsor: (id: string) => Promise<void>;
  addSponsor: (sponsorData: Omit<Sponsor, 'id' | 'created_at'>) => Promise<void>;
  updateSponsor: (id: string, sponsorData: Partial<Sponsor>) => Promise<void>;
  addPlayer: (playerData: Omit<Player, 'id' | 'created_at'>) => Promise<void>;
  updatePlayer: (id: string, playerData: Partial<Player>) => Promise<void>;
  updateGame: (id: string, gameData: Partial<Game>) => Promise<void>;
  addGame: (gameData: Omit<Game, 'id' | 'created_at' | 'score'>) => Promise<void>;
  setAuthError: (error: string | null) => void;
  checkSession: () => Promise<void>;
}

export const useStore = create<StoreState>((set, get) => ({
  players: [],
  games: [],
  sponsors: [],
  isAuthenticated: false,
  authError: null,
  isLoading: false,

  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
  setAuthError: (error) => set({ authError: error }),

  login: async (email: string, password: string) => {
    set({ isLoading: true, authError: null });
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      set({ isAuthenticated: true });
    } catch (error: any) {
      console.error('Login error:', error);
      set({ authError: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ isAuthenticated: false });
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  checkSession: async () => {
    set({ isLoading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      set({ isAuthenticated: !!session });
    } catch (error) {
      console.error('Error checking session:', error);
      set({ isAuthenticated: false });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchData: async () => {
    set({ isLoading: true });
    try {
      console.log('Starting fetchData...');
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Fetch timeout')), 10000)
      );

      const fetchPromise = Promise.all([
        supabase.from('players').select('*').order('name'),
        supabase.from('games').select('*').order('date'),
        supabase.from('sponsors').select('*').order('display_order', { ascending: true }).order('name'),
      ]);

      const [playersRes, gamesRes, sponsorsRes] = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]) as any;

      console.log('Fetch results:', {
        playersCount: playersRes.data?.length,
        gamesCount: gamesRes.data?.length,
        sponsorsCount: sponsorsRes.data?.length,
        errors: {
          players: playersRes.error,
          games: gamesRes.error,
          sponsors: sponsorsRes.error
        }
      });

      if (playersRes.error) throw playersRes.error;
      if (gamesRes.error) throw gamesRes.error;
      if (sponsorsRes.error) throw sponsorsRes.error;

      set({
        players: (playersRes.data || []) as Player[],
        games: (gamesRes.data || []) as Game[],
        sponsors: (sponsorsRes.data || []) as Sponsor[],
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      set({
        players: [],
        games: [],
        sponsors: [],
      });
    } finally {
      console.log('fetchData complete, setting isLoading to false');
      set({ isLoading: false });
    }
  },

  deletePlayer: async (id: string) => {
    try {
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const { players } = get();
      set({ players: players.filter(player => player.id !== id) });
      showToast('Player deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting player:', error);
      showToast('Failed to delete player', 'error');
      throw error;
    }
  },

  deleteGame: async (id: string) => {
    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const { games } = get();
      set({ games: games.filter(game => game.id !== id) });
      showToast('Game deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting game:', error);
      showToast('Failed to delete game', 'error');
      throw error;
    }
  },

  deleteSponsor: async (id: string) => {
    try {
      const { error } = await supabase
        .from('sponsors')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const { sponsors } = get();
      set({ sponsors: sponsors.filter(sponsor => sponsor.id !== id) });
      showToast('Sponsor deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting sponsor:', error);
      showToast('Failed to delete sponsor', 'error');
      throw error;
    }
  },

  addSponsor: async (sponsorData) => {
    try {
      const { data, error } = await supabase
        .from('sponsors')
        .insert([sponsorData])
        .select()
        .single();

      if (error) throw error;

      const { sponsors } = get();
      set({ sponsors: [...sponsors, data] });
      showToast('Sponsor added successfully', 'success');
    } catch (error) {
      console.error('Error adding sponsor:', error);
      showToast('Failed to add sponsor', 'error');
      throw error;
    }
  },

  updateSponsor: async (id, sponsorData) => {
    try {
      const { data, error } = await supabase
        .from('sponsors')
        .update(sponsorData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const { sponsors } = get();
      set({
        sponsors: sponsors.map(sponsor =>
          sponsor.id === id ? { ...sponsor, ...data } : sponsor
        ),
      });
      showToast('Sponsor updated successfully', 'success');
    } catch (error) {
      console.error('Error updating sponsor:', error);
      showToast('Failed to update sponsor', 'error');
      throw error;
    }
  },

  addPlayer: async (playerData) => {
    try {
      const { data, error } = await supabase
        .from('players')
        .insert([playerData])
        .select()
        .single();

      if (error) throw error;

      const { players } = get();
      set({ players: [...players, data] });
      showToast('Player added successfully', 'success');
    } catch (error) {
      console.error('Error adding player:', error);
      showToast('Failed to add player', 'error');
      throw error;
    }
  },

  updatePlayer: async (id, playerData) => {
    try {
      const { data, error } = await supabase
        .from('players')
        .update(playerData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const { players } = get();
      set({
        players: players.map(player =>
          player.id === id ? { ...player, ...data } : player
        ),
      });
      showToast('Player updated successfully', 'success');
    } catch (error) {
      console.error('Error updating player:', error);
      showToast('Failed to update player', 'error');
      throw error;
    }
  },

  updateGame: async (id, gameData) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .update(gameData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const { games } = get();
      set({
        games: games.map(game =>
          game.id === id ? { ...game, ...data } : game
        ),
      });
      showToast('Game updated successfully', 'success');
    } catch (error) {
      console.error('Error updating game:', error);
      showToast('Failed to update game', 'error');
      throw error;
    }
  },

  addGame: async (gameData) => {
    try {
      const { data, error } = await supabase
        .from('games')
        .insert([gameData])
        .select()
        .single();

      if (error) throw error;

      const { games } = get();
      set({ games: [...games, data] });
      showToast('Game added successfully', 'success');
    } catch (error) {
      console.error('Error adding game:', error);
      showToast('Failed to add game', 'error');
      throw error;
    }
  }
}));